import React, { useMemo } from 'react';

/**
 * ReconciliationMetrics
 *
 * 6 clickable metric cards for the Reconciliation Monitor.
 * Each card represents one class of data-integrity issue.
 *
 * Props:
 *   counts       { orphaned, failedReconciling, permanentFailed, stuck, slaBreach, reconciledToday }
 *   activeFilter string  — currently active filter key, or 'all'
 *   onFilter     (key) => void
 *   strings      i18n object (strings.reconciliationMonitor)
 */

const CARDS = [
  {
    key: 'orphaned',
    icon: '💳',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    textColor: 'text-purple-800',
    countColor: 'text-purple-700',
    activeBg: 'bg-purple-100',
    labelKey: 'metricOrphaned',
    defaultLabel: 'Orphaned Payments',
    titleKey: 'metricOrphanedTitle',
    defaultTitle: 'Paid but no shipment started (>15 min)',
  },
  {
    key: 'failedReconciling',
    icon: '🔄',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    textColor: 'text-orange-800',
    countColor: 'text-orange-700',
    activeBg: 'bg-orange-100',
    labelKey: 'metricFailedReconciling',
    defaultLabel: 'Failed Reconciling',
    titleKey: 'metricFailedReconcilingTitle',
    defaultTitle: 'Shipment creation retrying',
  },
  {
    key: 'permanentFailed',
    icon: '❌',
    bg: 'bg-red-50',
    border: 'border-red-200',
    textColor: 'text-red-800',
    countColor: 'text-red-700',
    activeBg: 'bg-red-100',
    labelKey: 'metricPermanentFailed',
    defaultLabel: 'Permanent Failed',
    titleKey: 'metricPermanentFailedTitle',
    defaultTitle: 'All retry attempts exhausted',
  },
  {
    key: 'stuck',
    icon: '⏳',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    textColor: 'text-yellow-800',
    countColor: 'text-yellow-700',
    activeBg: 'bg-yellow-100',
    labelKey: 'metricStuck',
    defaultLabel: 'Stuck (>10 min)',
    titleKey: 'metricStuckTitle',
    defaultTitle: 'Processing longer than threshold',
  },
  {
    key: 'slaBreach',
    icon: '⚠️',
    bg: 'bg-orange-50',
    border: 'border-orange-300',
    textColor: 'text-orange-900',
    countColor: 'text-orange-800',
    activeBg: 'bg-orange-100',
    labelKey: 'metricSlaBreach',
    defaultLabel: 'SLA Breached',
    titleKey: 'metricSlaBreachTitle',
    defaultTitle: 'Deadline passed, not completed',
  },
  {
    key: 'reconciledToday',
    icon: '✅',
    bg: 'bg-green-50',
    border: 'border-green-200',
    textColor: 'text-green-800',
    countColor: 'text-green-700',
    activeBg: 'bg-green-100',
    labelKey: 'metricReconciled',
    defaultLabel: 'Reconciled Today',
    titleKey: 'metricReconciledTitle',
    defaultTitle: 'Successfully resolved in last 24h',
  },
];

export default function ReconciliationMetrics({ counts = {}, activeFilter = 'all', onFilter, strings: s = {} }) {
  const cards = useMemo(() => CARDS.map((c) => ({
    ...c,
    count: counts[c.key] ?? 0,
    label: s[c.labelKey] || c.defaultLabel,
    title: s[c.titleKey] || c.defaultTitle,
  })), [counts, s]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      {cards.map((card) => {
        const isActive = activeFilter === card.key;
        return (
          <button
            key={card.key}
            onClick={() => onFilter && onFilter(isActive ? 'all' : card.key)}
            title={card.title}
            className={`
              text-left p-4 rounded-2xl border transition-all duration-200 w-full
              ${card.bg} ${card.border}
              ${isActive ? `${card.activeBg} ring-2 ring-secondary shadow-md` : 'hover:shadow-md'}
            `}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xl">{card.icon}</span>
              {isActive && (
                <span className="text-[10px] font-sans font-semibold px-1.5 py-0.5 rounded-full bg-secondary text-white leading-tight">
                  active
                </span>
              )}
            </div>
            <p className={`text-2xl font-heading font-bold ${card.countColor}`}>{card.count}</p>
            <p className={`text-xs font-sans mt-1 leading-tight ${card.textColor}`}>{card.label}</p>
          </button>
        );
      })}
    </div>
  );
}
